import { useCallback, useMemo, useState } from 'react';
import type { DragEvent } from 'react';
import { designLayoutCss, type DesignedProps } from '@sarnia/shared';
import { Icon } from '../../../components/ui/Icon';
import type { PanelCtx } from '../panelTypes';
import ExtrasPanel from './ExtrasPanel';
import './panels.css';

interface SectionsPanelProps {
  ctx: PanelCtx;
}

const SECTION_LABELS: Record<string, string> = {
  header: 'Navigation bar',
  footer: 'Footer'
};

function friendlyLabel(label: string): string {
  if (SECTION_LABELS[label]) return SECTION_LABELS[label];
  const words = label.split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'Section';
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export default function SectionsPanel({ ctx }: SectionsPanelProps) {
  const sectionCount = ctx.entry.sections.length;
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const fullOrder = useMemo(() => {
    const seen = new Set<number>();
    const result: number[] = [];
    for (const index of ctx.props.order) {
      if (Number.isInteger(index) && index >= 0 && index < sectionCount && !seen.has(index)) {
        seen.add(index);
        result.push(index);
      }
    }
    for (let i = 0; i < sectionCount; i += 1) {
      if (!seen.has(i)) result.push(i);
    }
    return result;
  }, [ctx.props.order, sectionCount]);

  const commit = useCallback((patch: Partial<DesignedProps>) => {
    const nextProps = { ...ctx.props, ...patch };
    ctx.update(patch);
    ctx.post({ type: 'layout', css: designLayoutCss(nextProps, sectionCount) });
  }, [ctx.props, ctx.update, ctx.post, sectionCount]);

  const toggleHidden = useCallback((sectionIndex: number) => {
    const current = new Set(ctx.props.hidden);
    if (current.has(sectionIndex)) {
      current.delete(sectionIndex);
    } else {
      current.add(sectionIndex);
    }
    const hidden = Array.from(current).sort((a, b) => a - b);
    commit({ hidden });
  }, [ctx.props.hidden, commit]);

  const move = useCallback((position: number, direction: -1 | 1) => {
    const target = position + direction;
    if (target < 0 || target >= fullOrder.length) return;
    const nextOrder = [...fullOrder];
    const [moved] = nextOrder.splice(position, 1);
    if (moved === undefined) return;
    nextOrder.splice(target, 0, moved);
    commit({ order: nextOrder });
  }, [fullOrder, commit]);

  const reset = useCallback(() => {
    commit({ hidden: [], order: [] });
  }, [commit]);

  const locate = useCallback((sectionIndex: number) => {
    ctx.post({ type: 'focus', selector: `[data-sec="${sectionIndex}"]` });
  }, [ctx.post]);

  const handleDragStart = useCallback((index: number, event: DragEvent<HTMLLIElement>) => {
    setDragIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLLIElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((targetIndex: number, event: DragEvent<HTMLLIElement>) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const nextOrder = [...fullOrder];
    const [moved] = nextOrder.splice(dragIndex, 1);
    if (moved === undefined) {
      setDragIndex(null);
      return;
    }
    nextOrder.splice(targetIndex, 0, moved);
    commit({ order: nextOrder });
    setDragIndex(null);
  }, [dragIndex, fullOrder, commit]);

  return (
    <div className="dwp-panel">
      <h2 className="dwp-heading">Sections</h2>
      <p className="dwp-help">Show, hide and reorder the sections of your page.</p>
      <button
        type="button"
        className="dwp-add-shortcut"
        onClick={() => document.querySelector('.dwp-extras')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      >
        <Icon name="plus" size={14} />
        <span>Add a new section</span>
      </button>
      <ul className="dwp-section-list">
        {fullOrder.map((sectionIndex, position) => {
          const rawLabel = ctx.entry.sections[sectionIndex]?.label ?? '';
          const label = friendlyLabel(rawLabel);
          const hidden = ctx.props.hidden.includes(sectionIndex);
          return (
            <li
              key={sectionIndex}
              className={`dwp-section-row${hidden ? ' is-hidden' : ''}${dragIndex === position ? ' is-dragging' : ''}`}
              draggable
              onDragStart={(event) => handleDragStart(position, event)}
              onDragOver={handleDragOver}
              onDrop={(event) => handleDrop(position, event)}
            >
              <span className="dwp-grip" aria-hidden="true">
                <Icon name="move" size={16} />
              </span>
              <span className="dwp-section-name" title={label}>
                {label}
                {hidden && <span className="dwp-badge">Hidden</span>}
              </span>
              <span className="dwp-row-actions">
                <button
                  type="button"
                  className="dwp-icon-btn"
                  aria-pressed={!hidden}
                  aria-label={`${hidden ? 'Show' : 'Hide'} ${label}`}
                  onClick={() => toggleHidden(sectionIndex)}
                >
                  <Icon name="eye" size={16} />
                </button>
                <button
                  type="button"
                  className="dwp-icon-btn"
                  disabled={position === 0}
                  aria-label={`Move ${label} up`}
                  onClick={() => move(position, -1)}
                >
                  <Icon name="chevron-down" size={16} style={{ transform: 'rotate(180deg)' }} />
                </button>
                <button
                  type="button"
                  className="dwp-icon-btn"
                  disabled={position === fullOrder.length - 1}
                  aria-label={`Move ${label} down`}
                  onClick={() => move(position, 1)}
                >
                  <Icon name="chevron-down" size={16} />
                </button>
                <button
                  type="button"
                  className="dwp-locate"
                  onClick={() => locate(sectionIndex)}
                >
                  <Icon name="search" size={14} />
                  <span>Locate</span>
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      <button type="button" className="dwp-reset" onClick={reset}>
        <Icon name="undo" size={14} />
        <span>Reset order and visibility</span>
      </button>
      <ExtrasPanel ctx={ctx} />
    </div>
  );
}
